import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Check, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1/sentinel`;

export default function WatchedAccounts() {
  const [handles, setHandles] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // {type: 'success'|'error', msg: string}
  const [initialLoading, setInitialLoading] = useState(true);

  // Fetch current watchlist
  const fetchWatchlist = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/watchlist`);
      const accounts = res.data.watched_accounts || [];
      setHandles(accounts.map(a => `@${a.handle}`).join("\n"));
    } catch (err) {
      console.error("Failed to fetch watchlist:", err);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  const handleUpdate = async () => {
    const parsed = handles
      .split(/[\n,]+/)
      .map(h => h.trim())
      .filter(h => h.length > 0);

    if (parsed.length === 0) {
      setStatus({ type: "error", msg: "At least one handle required" });
      return;
    }

    setLoading(true);
    setStatus(null);

    try {
      const res = await axios.put(`${API}/watchlist`, { handles: parsed });
      const ruleSync = res.data.rule_sync;
      let msg = `Updated ${res.data.accounts.length} accounts`;
      if (ruleSync) {
        msg += ` (${ruleSync})`;
      }
      setStatus({ type: "success", msg });
      setTimeout(() => setStatus(null), 5000);
    } catch (err) {
      setStatus({
        type: "error",
        msg: err.response?.data?.detail || "Failed to update watchlist",
      });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) return null;

  return (
    <div
      data-testid="watched-accounts-panel"
      className="rounded-[10px] border overflow-hidden"
      style={{ backgroundColor: '#161b22', borderColor: '#30363d' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: '#30363d' }}
      >
        <span
          className="font-mono text-xs tracking-[0.12em] uppercase"
          style={{ color: '#8b949e' }}
        >
          Watched Accounts
        </span>
      </div>

      {/* Body */}
      <div className="p-3 space-y-2">
        <textarea
          data-testid="watched-accounts-textarea"
          value={handles}
          onChange={(e) => setHandles(e.target.value)}
          placeholder="@zerohedge\n@FirstSquawk\n@DeItaone"
          className="w-full px-3 py-2 rounded-[6px] text-xs font-mono outline-none resize-none"
          style={{
            backgroundColor: '#1c2333',
            border: '1px solid #30363d',
            color: '#c9d1d9',
            minHeight: '100px',
          }}
          onFocus={(e) => (e.target.style.borderColor = '#484f58')}
          onBlur={(e) => (e.target.style.borderColor = '#30363d')}
        />

        {/* Status */}
        {status && (
          <div
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-[6px] text-[11px] font-mono"
            style={{
              backgroundColor: status.type === 'success' ? '#0a2a1a' : '#2a1418',
              border: `1px solid ${status.type === 'success' ? '#14532d' : '#7f1d1d'}`,
              color: status.type === 'success' ? '#22c55e' : '#fca5a5',
            }}
          >
            {status.type === 'success' ? <Check size={12} /> : <AlertCircle size={12} />}
            {status.msg}
          </div>
        )}

        <button
          data-testid="update-stream-rules-button"
          onClick={handleUpdate}
          disabled={loading}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-[6px] font-mono text-[11px] tracking-[0.08em] uppercase font-medium transition-colors duration-150"
          style={{
            backgroundColor: loading ? '#1c2333' : '#161b22',
            border: '1px solid #30363d',
            color: loading ? '#484f58' : '#c9d1d9',
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = '#1c2333';
              e.currentTarget.style.borderColor = '#22c55e';
              e.currentTarget.style.color = '#22c55e';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = '#161b22';
              e.currentTarget.style.borderColor = '#30363d';
              e.currentTarget.style.color = '#c9d1d9';
            }
          }}
        >
          <RefreshCw size={12} className={loading ? 'cw-spin' : ''} />
          {loading ? 'Updating...' : 'Update Stream Rules'}
        </button>
      </div>
    </div>
  );
}
